import { UseMutationOptions, useQueryClient } from 'react-query';
import produce from 'immer';

import {
  AdminUpdateClientMutation,
  AdminUpdateClientMutationVariables,
  ClientUrlParamsFragment,
  GetAllClientUrlParamsQuery,
  ParamsStatus,
  useGetAllClientUrlParamsQuery,
} from 'api';
import { useBootstrapQueryKey, useNotify } from 'hooks';

type Mutation = AdminUpdateClientMutation;
type MutationVariables = AdminUpdateClientMutationVariables;
type QueryData = GetAllClientUrlParamsQuery;

const ID_STUB = '-1';

const useCreateContentDomain = () => {
  const queryClient = useQueryClient();

  const queryKey = useGetAllClientUrlParamsQuery.getKey();
  const bootstrapQueryKey = useBootstrapQueryKey();
  const notify = useNotify();

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    // TODO: Insert into correct alphabetical position. Ask backend to sort
    onMutate: async mutationArgs => {
      await queryClient.cancelQueries(queryKey);

      const [contentDomain] = mutationArgs.data.content_domains;
      const newUrlParam: ClientUrlParamsFragment = {
        id: ID_STUB,
        configuration: null,
        content_domain: contentDomain,
        params_status: ParamsStatus.Active,
      };

      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          draftState.getAllClientUrlParams.unshift(newUrlParam);
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
    onSettled: () => {
      void queryClient.invalidateQueries(bootstrapQueryKey);
    },
  };

  return mutationOptions;
};

export default useCreateContentDomain;
